import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { congesApi, type AbsenceCalendrier } from '../../services/conges-api';
import { NavigationMois, moisDecale } from '../../components/ndf/ui/NavigationMois';
import { moisCourant, useMoisDeSession } from '../../hooks/useMoisDeSession';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { BoutonSecondaire, Champ, Selection } from '../../components/ndf/ui/Champ';
import { LIBELLE_STATUT } from './CongesDemandesPage';

/**
 * Le calendrier des absences de l'équipe (R-CAL-02) ou de toute l'entreprise
 * (R-CAL-03).
 *
 * UN SEUL écran pour les deux : ils ne diffèrent que par le périmètre, et le
 * périmètre est décidé par le serveur. Le titre s'ajuste à ce qu'on reçoit.
 *
 * Ce que cet écran sert à faire : voir qui sera là, et repérer les
 * chevauchements avant de trancher. Les demandes qui attendent MA décision sont
 * donc saillantes et mènent directement à l'écran de validation ; celles que je
 * ne peux pas trancher restent lisibles mais discrètes, signaler une attente
 * qu'on ne peut pas lever n'étant qu'un reproche.
 *
 * Le manager voit le TYPE d'absence (D5.5) et rien de plus : aucun document, ni
 * la moindre donnée médicale, ne transite par cette route.
 */

const iso = (a: number, m: number, j: number) =>
  `${a}-${String(m + 1).padStart(2, '0')}-${String(j).padStart(2, '0')}`;

/** La couleur d'une case, par statut. Constante avec le reste du module. */
const TEINTE: Record<string, string> = {
  attente_manager: 'bg-jaune-fond/60 text-jaune',
  attente_rh: 'bg-jaune-fond/60 text-jaune',
  approuvee: 'bg-vert-fond/60 text-vert',
  // Barrées et grisées, comme sur le calendrier du salarié : la constance des
  // couleurs entre les écrans est ce qui rend un statut lisible d'un coup d'œil.
  refusee: 'bg-slate-100 text-slate-400 line-through',
  annulee: 'bg-slate-100 text-slate-400 line-through',
};

/**
 * L'absence qui couvre ce jour, s'il y en a une.
 *
 * Une demande VIVANTE prime sur une demande close le même jour : ce qui compte
 * d'abord est de savoir qui sera là.
 */
function absenceDuJour(absences: AbsenceCalendrier[], jour: string): AbsenceCalendrier | null {
  const dessus = absences.filter((a) => a.date_debut <= jour && a.date_fin >= jour);
  return (
    dessus.find((a) => !['refusee', 'annulee'].includes(a.statut)) ?? dessus[0] ?? null
  );
}

export function CongesCalendrierEquipePage() {
  // Le mois de travail survit à un aller-retour vers un autre écran
  // (Lot 7, point 10). Les deux nombres en sont dérivés : le reste de l'écran
  // raisonne en année et en index de mois.
  const [moisIso, setMoisIso] = useMoisDeSession('calendrier-equipe', moisCourant());
  const annee = Number(moisIso.slice(0, 4));
  const mois = Number(moisIso.slice(5, 7)) - 1;
  const [filtreSalarie, setFiltreSalarie] = useState('');
  const [filtreType, setFiltreType] = useState('');

  const debut = iso(annee, mois, 1);
  const nbJours = new Date(Date.UTC(annee, mois + 1, 0)).getUTCDate();
  const fin = iso(annee, mois, nbJours);

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'calendrier', debut, fin],
    queryFn: () => congesApi.demandes.calendrier(debut, fin),
  });

  const typesPresents = useMemo(
    () =>
      [
        ...new Set((data?.lignes ?? []).flatMap((l) => l.absences.map((a) => a.type_libelle))),
      ].sort(),
    [data],
  );

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;
  if (!data) return null;

  const feries = new Set(data.feries);
  const jours = Array.from({ length: nbJours }, (_, i) => iso(annee, mois, i + 1));
  const chome = (j: string) => {
    const d = new Date(`${j}T00:00:00Z`).getUTCDay();
    return d === 0 || d === 6 || feries.has(j);
  };

  const lignes = data.lignes
    .filter((l) => !filtreSalarie || l.salarie_id === filtreSalarie)
    .map((l) => ({
      ...l,
      absences: filtreType ? l.absences.filter((a) => a.type_libelle === filtreType) : l.absences,
    }));

  const changerMois = (n: number) => setMoisIso(moisDecale(moisIso, n));

  const aValider = lignes.flatMap((l) => l.absences.filter((a) => a.a_valider_par_moi));

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-titre text-xl font-bold text-nuit">
            {data.perimetre_complet ? 'Calendrier de l’entreprise' : 'Calendrier de mon équipe'}
          </h1>
          <p className="text-sm text-nuit/60">
            {lignes.length} personne(s) sur la période affichée.
          </p>
        </div>
        <NavigationMois valeur={moisIso} onChange={setMoisIso} />
      </header>

      {aValider.length > 0 && (
        <p className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm text-jaune">
          {aValider.length} demande(s) de cette période attendent votre décision.{' '}
          <Link to="/conges/a-valider" className="font-medium underline">
            Les traiter
          </Link>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Champ label="Filtrer par personne">
          <Selection value={filtreSalarie} onChange={(e) => setFiltreSalarie(e.target.value)}>
            <option value="">Tout le monde</option>
            {data.lignes.map((l) => (
              <option key={l.salarie_id} value={l.salarie_id}>
                {l.nom}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Filtrer par type">
          <Selection value={filtreType} onChange={(e) => setFiltreType(e.target.value)}>
            <option value="">Tous les types</option>
            {typesPresents.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Selection>
        </Champ>
      </div>

      {/* Le tableau défile horizontalement plutôt que de comprimer les cases :
          trente et un jours ne tiennent pas sur un téléphone, et une case
          illisible ne vaut pas mieux qu'une case absente. */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 border-b border-slate-200 bg-white px-3 py-2 text-left font-medium text-nuit/70">
                Personne
              </th>
              {jours.map((j) => (
                <th
                  key={j}
                  className={`border-b border-slate-200 px-0 py-2 text-center text-xs font-medium ${
                    chome(j) ? 'bg-slate-50 text-slate-300' : 'text-nuit/60'
                  }`}
                >
                  {Number(j.slice(8))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.salarie_id}>
                <td className="sticky left-0 z-10 whitespace-nowrap border-t border-slate-100 bg-white px-3 py-1.5 text-nuit">
                  {l.nom}
                </td>
                {jours.map((j) => {
                  const a = absenceDuJour(l.absences, j);
                  const jourChome = chome(j);
                  const cellule = (
                    <span
                      className={`block h-6 w-6 rounded text-center text-[10px] leading-6 ${
                        jourChome
                          ? 'bg-slate-50'
                          : a
                            ? `${TEINTE[a.statut] ?? 'bg-slate-100'} cursor-pointer hover:opacity-80 ${
                                a.a_valider_par_moi ? 'ring-2 ring-jaune' : ''
                              }`
                            : ''
                      }`}
                      title={
                        a
                          ? `${l.nom} : ${a.type_libelle} (${LIBELLE_STATUT[a.statut] ?? a.statut})`
                          : undefined
                      }
                    >
                      {/* L'initiale du type : la couleur dit le statut, elle ne
                          dit pas de quelle absence il s'agit (D5.5). */}
                      {a && !jourChome ? a.type_libelle.slice(0, 1).toUpperCase() : ''}
                    </span>
                  );
                  return (
                    <td key={j} className="border-t border-slate-100 px-0.5 py-1.5 text-center">
                      {/* TOUTE case d'absence ouvre son détail (R-CAL-04), pas
                          seulement celles que je peux trancher : c'est de là
                          qu'on annule une absence approuvée, et une case morte
                          rendait cette action inatteignable. Les actions
                          offertes dans le détail restent celles du rôle. */}
                      {a && !jourChome ? (
                        <Link to={`/conges/demandes/${a.id}?depuis=/conges/calendrier-equipe`} className="block">
                          {cellule}
                        </Link>
                      ) : (
                        cellule
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {lignes.length === 0 && (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-nuit/60">
          Personne à afficher : votre périmètre ne couvre aucune fiche salarié.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-nuit/60">
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-vert-fond/60" /> approuvée
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-jaune-fond/60" /> en attente
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-jaune-fond/60 ring-2 ring-jaune" /> attend
          votre décision
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-slate-100 line-through" /> annulée ou
          rejetée
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-slate-50 ring-1 ring-slate-200" /> week-end
          ou férié
        </span>
        <span>La lettre est l’initiale du type d’absence. Cliquez une case pour ouvrir la demande.</span>
      </div>
    </div>
  );
}
